import React, { useMemo, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';

const idCards = [
  { id: 'personal-id', name: 'الهوية الشخصية', type: 'هوية', image: '/id-cards/personal-id.png', category: 'personal' },
  { id: 'public-security', name: 'الأمن العام', type: 'بطاقة شخصية عسكرية', image: '/id-cards/public-security.png', category: 'work' },
  { id: 'certified-employee', name: 'إدارة الموظف المعتمد', type: 'بطاقة العمل الشخصية', image: '/id-cards/certified-employee.png', category: 'work' },
  { id: 'inspection', name: 'إدارة الرقابة والتفتيش', type: 'بطاقة شخصية إدارية', image: '/id-cards/inspection.png', category: 'work' },
  { id: 'red-crescent', name: 'الهلال الأحمر', type: 'بطاقة العمل الشخصية', image: '/id-cards/red-crescent.png', category: 'work' },
  { id: 'border-guard', name: 'حرس الحدود', type: 'بطاقة العمل الشخصية', image: '/id-cards/border-guard.png', category: 'work' },
  { id: 'mechanic-garage', name: 'كراج الميكانيكي', type: 'بطاقة العمل الشخصية', image: '/id-cards/mechanic-garage.png', category: 'work' },
  { id: 'weapon-license', name: 'رخصة حمل سلاح', type: 'رخصة حمل سلاح', image: '/id-cards/weapon-license.png', category: 'weapon' },
  { id: 'driving-license', name: 'رخصة قيادة', type: 'رخصة قيادة', image: '/id-cards/driving-license.png', category: 'driving' },
];

const initialForm = {
  fullName: 'مواطن الراية',
  idNumber: '824',
  rank: 'متدرب',
  version: '1',
  birthDate: '1999/01/01',
  nationality: 'سعودي',
  birthPlace: 'مقاطعة الراية',
  issueDate: '15/05/2030',
  expiryDate: '05/01/2030',
  weaponType: 'مسدس',
  maker: 'امريكا',
  model: 'HAWK & LITTLE',
  caliber: '9MM',
  serial: 'ARB864',
  licenseType: 'خاصة',
  restrictions: 'بدون قيود',
};

function Field({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-gray-400 mb-2">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder || label}
        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-neon-cyan"
      />
    </div>
  );
}

function CardText({ className, children }) {
  if (!children) return null;

  return (
    <span
      className={`absolute z-20 overflow-hidden text-ellipsis whitespace-nowrap text-[clamp(8px,1.55vw,13px)] font-black leading-none text-white ${className}`}
      style={{ textShadow: '0 1px 2px #000, 0 0 4px #000' }}
    >
      {children}
    </span>
  );
}

export default function IdGenerator() {
  const [selectedCardId, setSelectedCardId] = useState(idCards[0].id);
  const [form, setForm] = useState(initialForm);
  const [imgError, setImgError] = useState(false);

  const selectedCard = useMemo(
    () => idCards.find((card) => card.id === selectedCardId) || idCards[0],
    [selectedCardId]
  );

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  const updateForm = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const handleCardChange = (e) => {
    setSelectedCardId(e.target.value);
    setImgError(false);
  };

  const isPersonal = selectedCard.category === 'personal';
  const isWork = selectedCard.category === 'work';
  const isWeapon = selectedCard.category === 'weapon';
  const isDriving = selectedCard.category === 'driving';

  return (
    <section id="الهوية" className="pt-32 pb-24 min-h-screen relative bg-[#050505] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(0,243,255,0.10),transparent_32%),radial-gradient(circle_at_80%_70%,rgba(188,19,254,0.12),transparent_35%)] pointer-events-none" />

      <div className="container mx-auto px-6 z-10 flex flex-col lg:flex-row items-center gap-12">
        <div className="lg:w-1/3 glass-panel p-8 rounded-3xl w-full max-h-[82vh] overflow-y-auto">
          <h2 className="text-3xl font-bold text-neon-cyan mb-8">صانع الهويات</h2>

          <div className="space-y-5">
            <div>
              <label className="block text-gray-400 mb-2">نوع البطاقة</label>
              <select
                value={selectedCardId}
                onChange={handleCardChange}
                className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-neon-cyan"
              >
                {idCards.map((card) => (
                  <option key={card.id} value={card.id}>
                    {card.name}
                  </option>
                ))}
              </select>
            </div>

            {isPersonal && (
              <>
                <Field label="الاسم الكامل" name="fullName" value={form.fullName} onChange={updateForm} />
                <Field label="الرقم" name="idNumber" value={form.idNumber} onChange={updateForm} />
                <Field label="تاريخ الميلاد" name="birthDate" value={form.birthDate} onChange={updateForm} />
                <Field label="الجنسية" name="nationality" value={form.nationality} onChange={updateForm} />
                <Field label="مكان الميلاد" name="birthPlace" value={form.birthPlace} onChange={updateForm} />
              </>
            )}

            {isWork && (
              <>
                <Field label="الرتبة" name="rank" value={form.rank} onChange={updateForm} />
                <Field label="الرقم" name="idNumber" value={form.idNumber} onChange={updateForm} />
                <Field label="النسخة" name="version" value={form.version} onChange={updateForm} />
              </>
            )}

            {isWeapon && (
              <>
                <Field label="الرقم" name="idNumber" value={form.idNumber} onChange={updateForm} />
                <Field label="نوع السلاح" name="weaponType" value={form.weaponType} onChange={updateForm} />
                <Field label="الصناعة" name="maker" value={form.maker} onChange={updateForm} />
                <Field label="الماركة" name="model" value={form.model} onChange={updateForm} />
                <Field label="العيار" name="caliber" value={form.caliber} onChange={updateForm} />
                <Field label="رقم السلاح" name="serial" value={form.serial} onChange={updateForm} />
                <Field label="تاريخ الانتهاء" name="expiryDate" value={form.expiryDate} onChange={updateForm} />
              </>
            )}

            {isDriving && (
              <>
                <Field label="الرقم" name="idNumber" value={form.idNumber} onChange={updateForm} />
                <Field label="تاريخ الميلاد" name="birthDate" value={form.birthDate} onChange={updateForm} />
                <Field label="تاريخ الإنشاء" name="issueDate" value={form.issueDate} onChange={updateForm} />
                <Field label="تاريخ الإصدار" name="expiryDate" value={form.expiryDate} onChange={updateForm} />
                <Field label="النوع" name="licenseType" value={form.licenseType} onChange={updateForm} />
                <Field label="القيود" name="restrictions" value={form.restrictions} onChange={updateForm} />
              </>
            )}
          </div>
        </div>

        <div className="lg:w-2/3 w-full flex justify-center perspective-[1000px]">
          <motion.div
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY }}
            className="relative w-full max-w-[620px] aspect-[443/258] rounded-2xl border border-white/20 shadow-neon-purple overflow-hidden group bg-black"
          >
            <img
              key={selectedCard.image}
              src={selectedCard.image}
              alt={selectedCard.name}
              className={`absolute inset-0 w-full h-full object-cover z-0 ${imgError ? 'hidden' : 'block'}`}
              onError={() => setImgError(true)}
            />

            {imgError && (
              <div className="absolute inset-0 bg-black/90 z-0 p-6 flex flex-col items-center justify-center text-center">
                <p className="text-red-400 font-bold mb-2">صورة البطاقة غير موجودة</p>
                <p className="text-gray-400 text-sm">{selectedCard.image}</p>
              </div>
            )}

            {!imgError && isPersonal && (
              <>
                <CardText className="left-[36%] top-[57.5%] w-[14%] text-left">{form.idNumber}</CardText>
                <CardText className="left-[26%] top-[65.5%] w-[24%] text-left">{form.birthDate}</CardText>
                <CardText className="left-[28%] top-[73.5%] w-[22%] text-left">{form.nationality}</CardText>
                <CardText className="left-[18%] top-[81.5%] w-[32%] text-left">{form.birthPlace}</CardText>
              </>
            )}

            {!imgError && isWork && (
              <>
                <CardText className="left-[63%] top-[65%] w-[14%] text-left">{form.rank}</CardText>
                <CardText className="left-[63%] top-[74%] w-[14%] text-left">{form.idNumber}</CardText>
                <CardText className="left-[63%] top-[83%] w-[14%] text-left">{form.version}</CardText>
              </>
            )}

            {!imgError && isWeapon && (
              <>
                <CardText className="left-[56%] top-[45%] w-[20%] text-left">{form.idNumber}</CardText>
                <CardText className="left-[49%] top-[55%] w-[27%] text-left">{form.weaponType}</CardText>
                <CardText className="left-[49%] top-[63%] w-[27%] text-left">{form.maker}</CardText>
                <CardText className="left-[42%] top-[71%] w-[34%] text-left">{form.model}</CardText>
                <CardText className="left-[54%] top-[79%] w-[22%] text-left">{form.caliber}</CardText>
                <CardText className="left-[49%] top-[87%] w-[27%] text-left">{form.serial}</CardText>
                <CardText className="left-[42%] bottom-[7%] w-[24%] text-left">{form.expiryDate}</CardText>
              </>
            )}

            {!imgError && isDriving && (
              <>
                <CardText className="left-[56%] top-[51%] w-[20%] text-left">{form.idNumber}</CardText>
                <CardText className="left-[49%] top-[60%] w-[27%] text-left">{form.birthDate}</CardText>
                <CardText className="left-[49%] top-[69%] w-[27%] text-left">{form.issueDate}</CardText>
                <CardText className="left-[49%] top-[77%] w-[27%] text-left">{form.expiryDate}</CardText>
                <CardText className="right-[16%] bottom-[7%] w-[18%] text-right">{form.licenseType}</CardText>
                <CardText className="left-[24%] bottom-[7%] w-[24%] text-left">{form.restrictions}</CardText>
              </>
            )}

            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-150%] group-hover:animate-[shimmer_2s_infinite] z-30 pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
